import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import ReportFrame from "./ReportFrame";

// ---------------------------------------------------------------------------
// src/pages/KPI/ReportViewerModal.js
//
// In-hub viewer for embeddable Power BI reports. Opens the selected report's
// publish-to-web URL inside a safe <ReportFrame>, with:
//   • Fullscreen toggle (reactstrap Modal `fullscreen`).
//   • Open in new tab.
//   • Graceful blocked-embed fallback (handled inside ReportFrame).
//
// Controlled by the caller: pass the `report` to show (or null) + onClose.
// Pure presentation.
//
// Props
//   report    the report object from reportConfig (or null when closed)
//   onClose   () => void
// ---------------------------------------------------------------------------

const ReportViewerModal = ({ report, onClose }) => {
    const [fullscreen, setFullscreen] = useState(false);
    const isOpen = !!report;

    const openNewTab = () => {
        if (report?.url) window.open(report.url, "_blank", "noopener,noreferrer");
    };

    // Reset fullscreen when the modal closes so the next open starts windowed.
    const handleClose = () => {
        setFullscreen(false);
        onClose?.();
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleClose}
            size="xl"
            fullscreen={fullscreen ? true : undefined}
            backdrop="static"
            scrollable
        >
            <ModalHeader toggle={handleClose} className="align-items-center">
                <span className="d-inline-flex align-items-center">
                    <i className={`${report?.icon || "ri-file-chart-line"} me-2 text-primary`} />
                    {report?.title}
                </span>
            </ModalHeader>
            <ModalBody style={{ height: fullscreen ? "calc(100vh - 120px)" : "75vh", padding: 0 }}>
                {report && (
                    <ReportFrame
                        src={report.url}
                        title={report.title}
                        onOpenNewTab={openNewTab}
                    />
                )}
            </ModalBody>
            <div className="d-flex justify-content-end gap-2 p-2 border-top">
                <Button color="light" onClick={() => setFullscreen((v) => !v)}>
                    <i className={`me-1 ${fullscreen ? "ri-fullscreen-exit-line" : "ri-fullscreen-line"}`} />
                    {fullscreen ? "Exit fullscreen" : "Fullscreen"}
                </Button>
                <Button color="primary" outline onClick={openNewTab}>
                    <i className="ri-external-link-line me-1" />
                    Open in new tab
                </Button>
                <Button color="secondary" onClick={handleClose}>
                    Close
                </Button>
            </div>
        </Modal>
    );
};

export default ReportViewerModal;
